<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Subject Areas
                </h2>
                <p>
                    A specified grouped area of content
                </p>
            </div>
            <div>
                <div class="flex items-center gap-3">
                    <!-- Search Filter -->
                    <div class="dropdown dropdown-end">
                        <d-btn flat size="sm">
                            <d-icon icon="filter" />
                        </d-btn>
                        <div tabindex="0" class="dropdown-content z-[1] card card-compact w-64 p-4 shadow bg-white">
                            <d-field label="Search by Name">
                                <d-input 
                                    v-model="filter" 
                                    placeholder="Type to search..."
                                    size="sm"
                                />
                            </d-field>
                        </div>
                    </div>
                    
                    <!-- Add Subject Area Button -->
                    <d-btn 
                        color="secondary"
                        @click="openSubjectAreaModal()"
                    >
                        <d-icon icon="plus" />
                        Add Subject Area
                    </d-btn>
                </div>
            </div>
        </div>
        
        <!-- Subject Areas Table -->
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="subjectAreas" 
            :columns="columns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            default-sort-direction="desc" 
            @sort="onSort"
        >
            <template v-slot="{ row }">
                <td>
                    <a :href="`/subject-areas/listing/${row.id}`" class="underline text-primary hover:text-primary-focus">
                        {{ row.name }}
                    </a>
                </td>
                <td>
                    <d-input 
                        v-model.number="row.order" 
                        type="number" 
                        min="1" 
                        class="w-16"
                        size="sm"
                        @blur="updateOrder(row.id, row.order)"
                    />
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openDuplicateModal(row)"
                    >
                        <d-icon icon="copy" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="gotoFilterEdit(row.id)"
                    >
                        <d-icon icon="tag" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openUsersModal(row)"
                    >
                        <d-icon icon="users" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openAccessModal(row)"
                    >
                        <d-icon icon="plus-square" />
                    </d-btn>
                </td>
                <td>
                    <d-btn
                        size="sm"
                        flat
                        color="primary"
                        @click="openSubjectAreaModal(row)"
                    >
                        <d-icon icon="edit" />
                    </d-btn>
                </td>
                <td v-if="hasRole('Super Admin')">
                    <d-btn
                        size="sm"
                        flat
                        color="danger"
                        @click="confirmDelete(row)"
                    >
                        <d-icon icon="trash" />
                    </d-btn>
                </td>
            </template>
        </d-table>
        
        <!-- Pagination -->
        <div class="flex justify-end mt-6">
            <d-pagination 
                :total-items="total" 
                :per-page="pagination.pageSize" 
                v-model:current="pagination.page" 
            />
        </div>

        <!-- Subject Area Modal -->
        <subject-area-modal
            :active="subjectAreaModalActive"
            :subject-area="selectedSubjectArea"
            @close="closeSubjectAreaModal"
            @saved="handleSubjectAreaSaved"
        />

        <!-- Duplicate Subject Area Modal -->
        <duplicate-subject-area-modal
            :active="duplicateModalActive"
            :subject-area="subjectAreaToDuplicate"
            @close="closeDuplicateModal"
            @saved="handleSubjectAreaSaved"
        />

        <!-- Users Modal -->
        <subject-area-user-modal
            :active="usersModalActive"
            :subject-area="selectedSubjectAreaForUsers"
            @close="closeUsersModal"
        />

        <!-- Access Modal -->
        <subject-area-access-modal
            :active="accessModalActive"
            :subject-area="selectedSubjectAreaForAccess"
            @close="closeAccessModal"
        />

        <!-- Delete Confirmation Modal -->
        <confirm-modal
            :active="deleteModalActive"
            :title="'Delete Subject Area'"
            :message="`Are you sure you want to delete <strong>${subjectAreaToDelete?.name}</strong>?<br><br>This action cannot be undone.`"
            :confirm-text="'Delete Subject Area'"
            @close="closeDeleteModal"
            @confirm="handleDeleteConfirmed"
        />
    </div>
</template>

<script>
  import { useAuthStore } from '@stores/auth';
  import axios from 'axios';
  import { toast } from '@services/toast.service';
  import SubjectAreaModal from './components/SubjectAreaModal.vue';
  import DuplicateSubjectAreaModal from './components/DuplicateSubjectAreaModal.vue';
  import ConfirmModal from '../Users/components/ConfirmModal.vue';
  import SubjectAreaUserModal from './components/SubjectAreaUserModal.vue';
  import SubjectAreaAccessModal from './components/SubjectAreaAccessModal.vue';

  export default {
    name: 'SubjectAreaListingPage',
    
    components: {
      SubjectAreaModal,
      DuplicateSubjectAreaModal,
      ConfirmModal,
      SubjectAreaUserModal,
      SubjectAreaAccessModal,
    },

    data() {
      return {
        subjectAreas: [],
        loading: false,
        filter: '',
        total: 0,
        subjectAreaModalActive: false,
        selectedSubjectArea: null,
        duplicateModalActive: false,
        subjectAreaToDuplicate: null,
        usersModalActive: false,
        selectedSubjectAreaForUsers: null,
        accessModalActive: false,
        selectedSubjectAreaForAccess: null,
        deleteModalActive: false,
        subjectAreaToDelete: null,
        pagination: {
          page: 1,
          pageSize: 20,
          sortField: 'order',
          sortOrder: 'asc',
        },
      };
    },

    watch: {
      pagination: {
        handler() {
          this.getSubjectAreasPaged();
        },
        deep: true,
      },
      filter() {
        this.pagination.page = 1; // Reset to first page when filtering
        this.getSubjectAreasPaged();
      },
    },

    mounted() {
      this.getSubjectAreasPaged();
    },

    computed: {
        columns() {
            const cols = [
                { field: 'name', label: 'Subject Area', sortable: true },
                { field: 'order', label: 'Order' },
                { field: 'copy', label: 'Duplicate' },
                { field: 'categories', label: 'Categories' },
                { field: 'users', label: 'Users' },
                { field: 'access', label: 'Access' },
                { field: 'edit', label: 'Edit' },
            ];

            if (this.hasRole('Super Admin')) {
                cols.push({ field: 'delete', label: 'Delete' });
            }

            return cols;
        },
        authStore() {
          return useAuthStore();
        },
    },

    methods: {
      hasRole(role) {
        return this.authStore.hasRole(role);
      },

      async getSubjectAreasPaged() {
        const { page, sortField, sortOrder, pageSize } = this.pagination;
        const params = [
          `filter=${encodeURIComponent(this.filter)}`,
          `sortDir=${sortField} ${sortOrder}`,
          `currentPage=${page}`,
          `pageSize=${pageSize}`
        ].join('&');
        
        this.loading = true;
        try {
          const { data } = await axios.get(`/api/subject-areas/paged?${params}`);
          this.subjectAreas = data.resultItems || [];
          this.total = data.totalRecords || 0;
        } catch (error) {
          console.error('Error loading subject areas:', error);
          toast.error('Failed to load subject areas');
          this.subjectAreas = [];
          this.total = 0;
        } finally {
          this.loading = false;
        }
      },

      openSubjectAreaModal(subjectArea = null) {
        this.selectedSubjectArea = subjectArea;
        this.subjectAreaModalActive = true;
      },

      closeSubjectAreaModal() {
        this.subjectAreaModalActive = false;
        this.selectedSubjectArea = null;
      },

      handleSubjectAreaSaved() {
        this.getSubjectAreasPaged();
        this.closeSubjectAreaModal();
        this.closeDuplicateModal();
      },

      openDuplicateModal(subjectArea) {
        this.subjectAreaToDuplicate = subjectArea;
        this.duplicateModalActive = true;
      },

      closeDuplicateModal() {
        this.duplicateModalActive = false;
        this.subjectAreaToDuplicate = null;
      },

      openUsersModal(subjectArea) {
        this.selectedSubjectAreaForUsers = subjectArea;
        this.usersModalActive = true;
      },

      closeUsersModal() {
        this.usersModalActive = false;
        this.selectedSubjectAreaForUsers = null;
      },

      openAccessModal(subjectArea) {
        this.selectedSubjectAreaForAccess = subjectArea;
        this.accessModalActive = true;
      },

      closeAccessModal() {
        this.accessModalActive = false;
        this.selectedSubjectAreaForAccess = null;
      },

      confirmDelete(subjectArea) {
        this.subjectAreaToDelete = subjectArea;
        this.deleteModalActive = true;
      },

      closeDeleteModal() {
        this.deleteModalActive = false;
        this.subjectAreaToDelete = null;
      },

      async handleDeleteConfirmed() {
        if (!this.subjectAreaToDelete) return;
        
        try {
          await axios.delete(`/api/subject-areas/${this.subjectAreaToDelete.id}`);
          await this.getSubjectAreasPaged();
          toast.success('Subject Area deleted successfully');
        } catch (error) {
          console.error('Error deleting subject area:', error);
          toast.error('Failed to delete subject area');
        } finally {
          this.closeDeleteModal();
        }
      },

      onSort(field, order) {
        this.pagination.sortField = field;
        this.pagination.sortOrder = order;
      },

      gotoFilterEdit(id) {
        window.location = `/subject-areas/listing/${id}/categories`;
      },

      async updateOrder(id, newOrder) {
        try {
          await axios.post('/api/subject-areas/update-order', {
            orderUpdates: [{ id, order: newOrder }]
          });
          toast.success('Order updated successfully');
        } catch (error) {
          console.error('Error updating order:', error);
          toast.error('Failed to update order');
          this.getSubjectAreasPaged(); // Reload to revert changes
        }
      },
    },
  };
</script>